// utils/device.js
import { Capacitor } from '@capacitor/core'
import { SystemPlugin } from '@/plugins/system'

/**
 * 获取设备平台
 * @returns {string} 'ios' | 'android' | 'web'
 */
export const getPlatform = () => {
  if (window.Capacitor) {
    return Capacitor.getPlatform()
  }
  // 开发环境默认返回 ios
  return 'ios'
}

// 原生安装来源检测结果缓存（initAndroidStore 成功后生效）
let nativeStoreCache = null

/**
 * 安装来源包名 → 商店标识
 * @param {string|null} installer 安装来源包名（adb 安装等情况为 null）
 * @returns {string|null} 命中的商店标识，未命中返回 null（继续走制造商兜底）
 */
const resolveStoreFromInstaller = (installer) => {
  if (!installer) return null

  const installerMap = {
    'com.huawei.hwid': 'huawei',       // 华为应用市场
    'com.huawei.appmarket': 'huawei',
    'com.xiaomi.mipbs': 'xiaomi',      // 小米应用商店
    'com.xiaomi.market': 'xiaomi',     // GetApps 国际版
    'com.heytap.market': 'oppo',       // OPPO 软件商店
    'com.oneplus.market': 'oppo',      // 一加商店（归 OPPO 渠道）
    'com.vivo.appstore': 'vivo',       // vivo 应用商店
    'com.bbk.appstore': 'vivo',
    'com.tencent.android.qqdownloader': 'tencent' // 腾讯应用宝
  }

  return installerMap[installer] || null
}

/**
 * 设备制造商 → 商店标识（安装来源缺失时兜底，如 adb 安装、Google Play 渠道）
 * @param {string|null} manufacturer 设备制造商（小写）
 * @returns {string} 商店标识
 */
const resolveStoreFromManufacturer = (manufacturer) => {
  if (!manufacturer) return 'default'

  if (manufacturer.includes('huawei') || manufacturer.includes('honor')) {
    return 'huawei'
  }
  if (manufacturer.includes('xiaomi') || manufacturer.includes('redmi')) {
    return 'xiaomi'
  }
  if (manufacturer.includes('oppo') || manufacturer.includes('oneplus') || manufacturer.includes('realme')) {
    return 'oppo'
  }
  if (manufacturer.includes('vivo') || manufacturer.includes('iqoo')) {
    return 'vivo'
  }

  // 其他品牌（三星、联想等）
  return 'default'
}

/**
 * 初始化 Android 安装来源检测（原生优先，结果缓存供同步调用）
 * 应在版本检测前调用；web/iOS 环境直接跳过
 */
export const initAndroidStore = async () => {
  if (getPlatform() !== 'android' || !Capacitor.isNativePlatform()) {
    return
  }

  try {
    const { installer, manufacturer } = await SystemPlugin.getInstallSource()
    nativeStoreCache = resolveStoreFromInstaller(installer) || resolveStoreFromManufacturer(manufacturer)
    console.log('📱 原生安装来源检测: installer =', installer, ', manufacturer =', manufacturer, ', store =', nativeStoreCache)
  } catch (error) {
    console.warn('⚠️ 原生安装来源检测失败，回退 UA 识别:', error)
  }
}

/**
 * 检测 Android 设备对应的应用商店
 * 优先使用原生安装来源检测结果，未初始化时回退 UA 识别
 * @returns {string} 'huawei' | 'xiaomi' | 'oppo' | 'vivo' | 'tencent' | 'default'
 */
export const detectAndroidStore = () => {
  if (nativeStoreCache) {
    return nativeStoreCache
  }

  const ua = navigator.userAgent.toLowerCase()

  // 华为/荣耀
  if (ua.includes('huawei') || ua.includes('honor')) {
    return 'huawei'
  }

  // 小米/红米
  if (ua.includes('mi ') || ua.includes('redmi') || ua.includes('xiaomi')) {
    return 'xiaomi'
  }

  // OPPO/一加
  if (ua.includes('oppo') || ua.includes('oneplus')) {
    return 'oppo'
  }

  // vivo/iQOO
  if (ua.includes('vivo') || ua.includes('iqoo')) {
    return 'vivo'
  }

  // 腾讯系（应用宝）
  if (ua.includes('tencent') || ua.includes('qq') || ua.includes('wenjuan')) {
    return 'tencent'
  }

  // 其他品牌（三星、联想等，使用应用宝）
  return 'default'
}

/**
 * 获取应用商店URL（根据设备品牌）
 * @param {string} packageName - 应用包名
 * @returns {string} 应用商店URL
 */
export const getStoreUrl = (packageName = 'com.kenzom.aroma') => {
  const store = detectAndroidStore()

  const storeUrls = {
    'huawei': `https://appgallery.huawei.com/#/app/${packageName}`,
    'xiaomi': `https://app.mi.com/details?id=${packageName}`,
    'oppo': `https://store.oppomobile.com/software/detail?id=${packageName}`,
    'vivo': `https://vivoapp.cn/details?id=${packageName}`,
    'tencent': `https://a.app.qq.com/o/simple.jsp?pkgname=${packageName}`,
    'other': `https://a.app.qq.com/o/simple.jsp?pkgname=${packageName}`
  }

  return storeUrls[store] || storeUrls['other']
}

/**
 * 深链拉起手机上的应用商店并直达 App 详情页（仅 Android）
 * 华为/荣耀优先用 AppGallery 专有 scheme appmarket://，再退 market://；
 * 其余机型 market:// 由系统路由到自带商店。
 * @param {string} packageName - 应用包名（须与商店上架的 applicationId 一致）
 * @returns {Promise<boolean>} 是否成功拉起商店 App（失败时调用方自行兜底网页版）
 */
export const openStoreApp = async (packageName) => {
  if (getPlatform() !== 'android' || !Capacitor.isNativePlatform() || !packageName) {
    return false
  }

  const store = detectAndroidStore()
  const schemes = store === 'huawei'
    ? [`appmarket://details?id=${packageName}`, `market://details?id=${packageName}`]
    : [`market://details?id=${packageName}`]

  for (const scheme of schemes) {
    try {
      await SystemPlugin.openSystemUrl({ url: scheme })
      console.log('✅ 已拉起商店 App:', scheme)
      return true
    } catch (e) {
      console.log('⚠️ 深链未命中商店 App:', scheme, e)
    }
  }
  return false
}

/**
 * 获取当前设备应用商店信息
 * @returns {Object} { platform, store }
 */
export const getDeviceInfo = () => {
  const platform = getPlatform()
  const store = platform === 'android' ? detectAndroidStore() : null

  return {
    platform,
    store,
    isNative: Capacitor.isNativePlatform(),
    // 是否是iOS
    isIOS: platform === 'ios',
    // 是否是Android
    isAndroid: platform === 'android'
  }
}
